import React, { useEffect, useState, useCallback } from 'react';
import Lottie from 'lottie-react';
import Sound from 'react-sound';
import moment from 'moment';
import filter from 'lodash/filter';
import orderBy from 'lodash/orderBy';
import find from 'lodash/find';
import pull from 'lodash/pull';
import reject from 'lodash/reject';
import confetti from '../../assets/animations/top-down-confetti.json';
import { useSnackbar } from 'material-ui-snackbar-provider';

import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import CloseIcon from '@material-ui/icons/Close';
import IconButton from '@material-ui/core/IconButton';
import EditIcon from '@material-ui/icons/Edit';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';

import FullScreenDialog, { FullScreenDialogProps } from '../FullScreenDialog';
import useCompanyContext from '../../hooks/useCompanyContext';
import useDialogState from '../../hooks/useDialogState';
import AddStampDialog from './AddStampDialog';
import AddRewardDialog from './AddRewardDialog';
import RedeemRewardDialog from './RedeemRewardDialog';
import RemoveStampDialog from './RemoveStampDialog';
import UpdateEmailDialog from './UpdateEmailDialog';
import UpdateMobileDialog from './UpdateMobileDialog';
import UpdateAlternateEmailDialog from './UpdateAlternateEmailDialog';
import UpdateNotesDialog from './UpdateNotesDialog';
import UpdateNameDialog from './UpdateNameDialog';
import UpdateDOBDialog from './UpdateDOBDialog';
import DeleteRewardDialog from './DeleteRewardDialog';
import DeleteMemberDialog from './DeleteMemberDialog';
import DeleteStampCardDialog from './DeleteStampCardDialog';
import AddCardDialog from './AddCardDialog';

import * as MemberService from '../../api/memberService';
import * as RewardCardService from '../../api/rewardCardService';
import * as StampCardService from '../../api/stampCardService';
import * as StampCampaignService from '../../api/stampCampaignService';
import * as StoreService from '../../api/storeService';

import RewardCard from '../Card/RewardCard';
import RedeemedRewardCard from '../Card/RedeemedRewardCard';
import Spinner from '../Spinner';
import StampCard from '../StampCard';
import BlankStampCard from '../BlankStampCard';
import { capitalize } from '../../tools/capitalize';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: theme.spacing(6),
  },
  logoContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  merchantLogo: {
    height: 120,
    width: 120,
    borderRadius: '50%',
    objectFit: 'cover',
    boxShadow: '0 2px 10px rgba(199, 199, 199, 0.5)',
  },
  header: {
    marginTop: 5,
    textAlign: 'center',
  },
  qr: {
    margin: theme.spacing(3),
  },
  box: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
  },
  close: {
    marginRight: 'auto',
  },
  closeIcon: {
    color: theme.palette.common.black,
  },
  rewardHeader: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    textAlign: 'center',
  },
  subText: {
    color: '#989AA2',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  editContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: '80%',
  },
  editBox: {
    marginTop: 5,
  },
  editNullBox: {
    width: 1,
    height: 29,
  },
  editIcon: {
    color: theme.palette.text.secondary,
    width: '14px',
    height: '14px',
  },
  circleIcon: {
    color: theme.palette.text.secondary,
    width: '24px',
    height: '24px',
  },
  iconContainer: {
    width: '15px',
    height: '15px',
  },
  rewardContainer: {
    display: 'flex',
    flexDirection: 'row',
  },
  addRewardBox: {
    marginTop: -55,
    position: 'relative',
    right: -140,
  },
  grow: {
    flexGrow: 1,
  },
  showDetailButton: {
    color: theme.palette.text.secondary,
    padding: 5,
  },
  showHistoryBox: {
    marginTop: -45,
    position: 'relative',
    right: -140,
  },
}));

type Props = Pick<FullScreenDialogProps, 'open'> & {
  onClose: () => void;
  memberId: string;
  store: StoreService.Store | null;
  isVolumeMute: boolean;
  role: string;
  maxSelectedNumber: number;
};

export default function MemberDetailsDialog({
  open,
  onClose,
  memberId,
  store,
  isVolumeMute,
  role,
  maxSelectedNumber,
}: Props) {
  const classes = useStyles();
  const snackbar = useSnackbar();
  const { company } = useCompanyContext();
  const [isLoading, setIsLoading] = useState(true);
  const [stampCardList, setStampCardList] = useState<
    StampCardService.StampCard[]
  >([]);
  const [milestoneList, setMilestoneList] = useState<
    StampCampaignService.Milestone[]
  >([]);
  const [addCardDialogOpen, openAddCardDialog, closeAddCardDialog] =
    useDialogState(false);
  const [addStampDialogOpen, openAddStampDialog, closeAddStampDialog] =
    useDialogState(false);
  const [addRewardDialogOpen, openAddRewardDialog, closeAddRewardDialog] =
    useDialogState(false);
  const [removeStampDialogOpen, openRemoveStampDialog, closeRemoveStampDialog] =
    useDialogState(false);
  const [redeemRewardOpenDialog, setRedeemRewardOpenDialog] = useState(false);
  const [deleteRewardOpenDialog, setDeleteRewardOpenDialog] = useState(false);
  const [deleteMemberOpenDialog, setDeleteMemberOpenDialog] = useState(false);
  const [deleteCardOpenDialog, setDeleteCardOpenDialog] = useState(false);
  const [editNotesOpenDialog, setEditNotesOpenDialog] = useState(false);
  const [editNameOpenDialog, setEditNameOpenDialog] = useState(false);
  const [editEmailOpenDialog, setEditEmailOpenDialog] = useState(false);
  const [editMobileOpenDialog, setEditMobileOpenDialog] = useState(false);
  const [editAltEmailOpenDialog, setEditAltEmailOpenDialog] = useState(false);
  const [editDOBOpenDialog, setEditDOBOpenDialog] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [collectedRewardCardList, setCollectedRewardCardList] = useState<
    RewardCardService.RewardCard[]
  >([]);
  const [redeemedRewardCardList, setRedeemedRewardCardList] = useState<
    RewardCardService.RewardCard[]
  >([]);
  const [selectedRedeemRewardCard, setSelectedRedeemRewardCard] =
    useState<RewardCardService.RewardCard | null>(null);
  const [selectedDeleteRewardCard, setSelectedDeleteRewardCard] =
    useState<RewardCardService.RewardCard | null>(null);
  const [soundFile, setSoundFile] = useState('ding.mp3');
  const [addedStamp, setAddedStamp] = useState(0);
  const [member, setMember] = useState<MemberService.Member | null>(null);
  const [user, setUser] = useState<MemberService.User | null>(null);
  const [historyExpand, setHistoryExpand] = useState(false);
  const [currentStampCampaignIdList, setCurrentStampCampaignIdList] = useState<
    string[]
  >([]);
  const [activeStampCampaignList, setActiveStampCampaignList] = useState<
    StampCampaignService.StampCampaign[]
  >([]);
  const [selectedStampCard, setSelectedStampCard] =
    useState<StampCardService.StampCard | null>(null);
  const isAdminOnly =
    role === 'company_admin' || role === 'manager' || role === 'owner'
      ? true
      : false;

  const fetchRedeemedRewardCard = useCallback(async () => {
    if (company && memberId) {
      try {
        const redeemedRewardCards = await RewardCardService.listRedeemed(
          company.id,
          memberId
        );

        const res = await RewardCardService.listExpiredCollected(
          company.id,
          memberId
        );

        const resExpired = await RewardCardService.listExpired(
          company.id,
          memberId
        );

        const now = parseInt(moment().format('X'), 10);

        const expiredRewardCards = orderBy(
          filter(res, function (card) {
            return card.expiresAt < now;
          }),
          ['expiresAt'],
          ['desc']
        );

        const historyRewardCards = orderBy(
          redeemedRewardCards,
          ['redeemedAt'],
          ['desc']
        );

        setRedeemedRewardCardList(
          orderBy(
            historyRewardCards.concat(expiredRewardCards.concat(resExpired)),
            ['createdAt'],
            ['desc']
          )
        );
      } catch (e) {
        console.log('error: ', e);
      }
    }
  }, [company, memberId]);

  const fetchCollectedRewardCard = useCallback(async () => {
    if (company && memberId) {
      try {
        const collectedRewardCards = await RewardCardService.listCollected(
          company.id,
          memberId
        );

        const now = parseInt(moment().format('X'), 10);

        setCollectedRewardCardList(
          filter(collectedRewardCards, function (card) {
            return card.expiresAt >= now;
          })
        );
        setIsLoading(false);
      } catch (e) {
        console.log('error: ', e);
        setIsLoading(false);
      }
    }
  }, [company, memberId]);

  useEffect(() => {
    const fetchStampCard = async () => {
      if (company && memberId) {
        try {
          const resStampCardList =
            await StampCardService.listMembershipStampCard(
              company.id,
              memberId
            );

          if (resStampCardList.length > 0) {
            setStampCardList(resStampCardList);

            let currStampCampaignList: string[] = [];
            try {
              resStampCardList.forEach(async (stampcard) => {
                const resMilestone = await StampCampaignService.listMilestones(
                  company.id,
                  stampcard.stamp_campaign.id
                );
                let newlist = milestoneList.concat(resMilestone);
                setMilestoneList(newlist);

                if (stampcard.status === 'active') {
                  currStampCampaignList.push(stampcard.stamp_campaign.id);
                }
              });

              setCurrentStampCampaignIdList(currStampCampaignList);

              fetchRedeemedRewardCard();

              setTimeout(() => fetchCollectedRewardCard(), 1500);
            } catch (e) {
              console.log('Error:', e);
            }
          } else {
            try {
              fetchRedeemedRewardCard();

              setTimeout(() => fetchCollectedRewardCard(), 1500);
            } catch (e) {
              console.log('Error:', e);
            }
          }
        } catch (e) {
          console.log('Error:', e);
          try {
            fetchRedeemedRewardCard();

            setTimeout(() => fetchCollectedRewardCard(), 1500);
          } catch (e) {
            console.log('Error:', e);
          }
        }
      }
    };

    const fetchActiveStampCampaignList = async () => {
      if (company) {
        const resActiveStampCampaignList =
          await StampCampaignService.listActive(company.id);

        if (
          resActiveStampCampaignList &&
          resActiveStampCampaignList.length > 0
        ) {
          if (currentStampCampaignIdList.length > 0) {
            let filteredActiveStampCampaignList = filter(
              resActiveStampCampaignList,
              function (activeStampCampaign) {
                return activeStampCampaign.status === 'active';
              }
            );

            setActiveStampCampaignList(filteredActiveStampCampaignList);
          } else {
            setActiveStampCampaignList(resActiveStampCampaignList);
          }
        }
      }
    };
    const fetch = async () => {
      if (company && memberId) {
        const resMember = await MemberService.getMember(company.id, memberId);

        if (resMember) {
          setMember(resMember);
        }

        const resUser = await MemberService.getUser(company.id, memberId);

        if (resUser) {
          setUser(resUser);
        }

        setTimeout(() => fetchStampCard(), 1500);

        setTimeout(() => fetchActiveStampCampaignList(), 1500);
      }
    };
    if (isLoading) {
      fetch();
    }
  }, [
    isLoading,
    company,
    memberId,
    milestoneList,
    fetchRedeemedRewardCard,
    fetchCollectedRewardCard,
    currentStampCampaignIdList,
  ]);

  const setRedeemReward = (rewardCard: RewardCardService.RewardCard) => {
    setSelectedRedeemRewardCard(rewardCard);
    setRedeemRewardOpenDialog(true);
  };

  const setDeleteReward = (rewardCard: RewardCardService.RewardCard) => {
    setSelectedDeleteRewardCard(rewardCard);
    setDeleteRewardOpenDialog(true);
  };

  const handleDeleteReward = () => {
    setDeleteRewardOpenDialog(false);
    setSelectedDeleteRewardCard(null);
    setIsLoading(true);
    snackbar.showMessage('Reward deleted');
  };

  const handleDeleteMember = async () => {
    if (company && memberId) {
      try {
        await MemberService.deleteMember({
          companyId: company.id,
          memberID: memberId,
        });
        setDeleteMemberOpenDialog(false);
        onClose();
        snackbar.showMessage('Member deleted');
      } catch (e) {
        console.log('error delete member:', e);
        setDeleteMemberOpenDialog(false);
        snackbar.showMessage('Error deleting member');
      }
    }
  };

  const handleRedeemReward = async () => {
    if (company && memberId && selectedRedeemRewardCard && store) {
      try {
        await RewardCardService.redeem({
          companyId: company.id,
          memberID: memberId,
          rewardCardId: selectedRedeemRewardCard.id,
          pin: store.pin,
        });

        setSoundFile('tada.mp3');
        setRedeemRewardOpenDialog(false);
        setSelectedRedeemRewardCard(null);
        setIsLoading(true);
        setIsPlaying(true);
        if (isVolumeMute) {
          setInterval(() => setIsPlaying(false), 5000);
        }
      } catch (e: any) {
        console.log('Error: ', e);
      }
    }
  };

  const handleDeleteStamp = async (removedStamp: number) => {
    if (
      company &&
      stampCardList &&
      stampCardList.length > 0 &&
      removedStamp > 0 &&
      selectedStampCard
    ) {
      try {
        await StampCardService.removeStamp({
          companyId: company.id,
          stampCardId: selectedStampCard.id,
          numOfStamps: removedStamp,
        });
        setIsLoading(true);
        snackbar.showMessage(
          removedStamp + ` Stamp${removedStamp > 1 ? 's' : ''} Removed`
        );
      } catch (e) {
        console.log('Error Delete Stamp: ', e);
      }
    }
  };

  const handleDeleteCard = async () => {
    if (company && selectedStampCard) {
      try {
        await StampCardService.archiveStampCard({
          companyId: company.id,
          cardId: selectedStampCard.id,
        });
        snackbar.showMessage('Card deleted!');
        let updatedStampCardList = reject(stampCardList, function (card) {
          return card.id === selectedStampCard.id;
        });
        setStampCardList(updatedStampCardList);
        console.log('Selected Stampcard:', selectedStampCard);

        pull(currentStampCampaignIdList, selectedStampCard.stamp_campaign.id);
        console.log('Update List:', currentStampCampaignIdList);
        setCurrentStampCampaignIdList(currentStampCampaignIdList);

        setSelectedStampCard(null);
        setDeleteCardOpenDialog(false);
      } catch (e) {
        console.log('Error Deactivate Stamp: ', e);
        snackbar.showMessage('Card not deleted!');
        setDeleteCardOpenDialog(false);
      }
    }
  };

  const handleReissueCard = async () => {
    if (!isLoading && company && memberId && selectedStampCard) {
      try {
        await StampCardService.issueCard({
          companyId: company.id,
          membershipID: memberId,
          stampCampaignIDs: [selectedStampCard.stamp_campaign.id],
        });
        setIsLoading(true);

        snackbar.showMessage('Card reissued!');
      } catch (e) {
        console.log('Error issue card: ', e);
      }
    }
  };

  if (isLoading) {
    return (
      <FullScreenDialog open={open}>
        <div className={classes.box}>
          <div className={classes.close}>
            <IconButton onClick={onClose}>
              <CloseIcon className={classes.closeIcon} />
            </IconButton>
          </div>
        </div>
        <Box display="flex" justifyContent="center">
          <Spinner />
        </Box>
      </FullScreenDialog>
    );
  } else {
    if (!company) {
      return null;
    }

    if (!milestoneList) {
      return null;
    }

    if (!activeStampCampaignList) {
      return null;
    }

    if (!store) {
      return null;
    }

    if (!member) {
      return null;
    }

    if (!user) {
      return null;
    }

    return (
      <FullScreenDialog open={open}>
        {isPlaying && (
          <Lottie
            animationData={confetti}
            loop={isPlaying}
            autoplay={false}
            style={{ left: 0, right: 0, position: 'absolute' }}
          />
        )}
        {!isVolumeMute && (
          <Sound
            url={`../sound/${soundFile}`}
            playStatus={isPlaying ? 'PLAYING' : 'STOPPED'}
            onFinishedPlaying={() => {
              setIsPlaying(false);
            }}
          />
        )}
        <div className={classes.box}>
          <div className={classes.close}>
            <IconButton onClick={onClose}>
              <CloseIcon className={classes.closeIcon} />
            </IconButton>
          </div>
        </div>
        <div className={classes.logoContainer}>
          <div className={classes.editContainer}>
            <Typography variant="h4" className={classes.header}>
              {user.firstName && user.lastName
                ? capitalize(
                    user.firstName.toLowerCase() +
                      ' ' +
                      user.lastName.toLowerCase()
                  )
                : user.firstName && !user.lastName
                ? capitalize(user.firstName.toLowerCase())
                : !user.firstName && user.lastName
                ? capitalize(user.lastName.toLowerCase())
                : 'Unknown Unknown'}
            </Typography>
            <div className={classes.editBox}>
              <IconButton onClick={() => setEditNameOpenDialog(true)}>
                <EditIcon className={classes.editIcon} fontSize="small" />
              </IconButton>
            </div>
          </div>
          <div className={classes.editContainer}>
            <Typography variant="body1" className={classes.header}>
              <strong>Stamps Collected (All Time):</strong> {member.totalStamps}
            </Typography>
            <div className={classes.editNullBox} />
          </div>

          {(user.email || (!user.email && isAdminOnly)) && (
            <div className={classes.editContainer}>
              <Typography variant="body1" className={classes.header}>
                <strong>Email:</strong> {user.email}
              </Typography>
              {isAdminOnly && (
                <div className={classes.editBox}>
                  <IconButton
                    onClick={() => setEditEmailOpenDialog(true)}
                    className={classes.iconContainer}
                  >
                    <EditIcon className={classes.editIcon} fontSize="small" />
                  </IconButton>
                </div>
              )}
            </div>
          )}
          {user.altEmail && (
            <div className={classes.editContainer}>
              <Typography variant="body1" className={classes.header}>
                <strong>Alternate email:</strong> {user.altEmail}
              </Typography>
              {isAdminOnly && (
                <div className={classes.editBox}>
                  <IconButton
                    onClick={() => setEditAltEmailOpenDialog(true)}
                    className={classes.iconContainer}
                  >
                    <EditIcon className={classes.editIcon} fontSize="small" />
                  </IconButton>
                </div>
              )}
            </div>
          )}
          {((user.mobNum && user.mobNum !== '<nil>') ||
            (!user.mobNum && isAdminOnly)) && (
            <div className={classes.editContainer}>
              <Typography variant="body1" className={classes.header}>
                <strong>Mobile:</strong> {user.mobNum}
              </Typography>
              {isAdminOnly && (
                <div className={classes.editBox}>
                  <IconButton
                    onClick={() => {
                      console.log('Mobile Edit');
                      setEditMobileOpenDialog(true);
                    }}
                    className={classes.iconContainer}
                  >
                    <EditIcon className={classes.editIcon} fontSize="small" />
                  </IconButton>
                </div>
              )}
            </div>
          )}
          <div className={classes.editContainer}>
            <Typography variant="body1" className={classes.header}>
              <strong>Birthday:</strong>{' '}
              {user.dob ? moment.unix(user.dob).format('DD MMM') : ''}
            </Typography>
            <div className={classes.editBox}>
              <IconButton
                onClick={() => setEditDOBOpenDialog(true)}
                className={classes.iconContainer}
              >
                <EditIcon className={classes.editIcon} fontSize="small" />
              </IconButton>
            </div>
          </div>
          <div className={classes.editContainer}>
            <Typography variant="body1" className={classes.header}>
              <strong>Date Joined:</strong>{' '}
              {moment.unix(member.createdAt).format('DD MMM YYYY')}
            </Typography>
            <div className={classes.editNullBox} />
          </div>
          <div className={classes.editContainer}>
            <Typography variant="body1" className={classes.header}>
              <strong>Notes:</strong>{' '}
              {member.notes && member.notes.length > 0 ? member.notes : ' '}
            </Typography>
            <div className={classes.editBox}>
              <IconButton
                onClick={() => setEditNotesOpenDialog(true)}
                className={classes.iconContainer}
              >
                <EditIcon className={classes.editIcon} fontSize="small" />
              </IconButton>
            </div>
          </div>
          <Typography variant="h4" className={classes.rewardHeader}>
            Cards
          </Typography>
          {isAdminOnly &&
            currentStampCampaignIdList.length !==
              activeStampCampaignList.length && (
              <div className={classes.addRewardBox}>
                <IconButton onClick={openAddCardDialog}>
                  <AddCircleIcon className={classes.circleIcon} />
                </IconButton>
              </div>
            )}
          {stampCardList.length === 0 && (
            <BlankStampCard
              handleAddStampCard={() => openAddCardDialog()}
              isAdminOnly={isAdminOnly}
            />
          )}
          {milestoneList &&
            activeStampCampaignList.length > 0 &&
            stampCardList.length > 0 &&
            stampCardList.map((card) => (
              <StampCard
                key={card.id}
                companyId={company.id}
                cardName={card.stampCampaignName}
                stampCampaignId={card.stamp_campaign.id}
                merchantId={company.id}
                maxStamps={card.redemptionCost}
                quantity={card.quantity}
                handleAddStamp={() => {
                  setSelectedStampCard(card);
                  openAddStampDialog();
                }}
                handleRemoveStamp={() => {
                  setSelectedStampCard(card);
                  openRemoveStampDialog();
                }}
                stampCardStatus={card.status}
                isAdminOnly={isAdminOnly}
                campaignType={
                  find(activeStampCampaignList, function (stampCampaign) {
                    return stampCampaign.id === card.stamp_campaign.id;
                  })
                    ? find(activeStampCampaignList, function (stampCampaign) {
                        return stampCampaign.id === card.stamp_campaign.id;
                      })!.type
                    : ''
                }
                isMultipleCardAllowed={
                  find(activeStampCampaignList, function (stampCampaign) {
                    return stampCampaign.id === card.stamp_campaign.id;
                  })
                    ? find(activeStampCampaignList, function (stampCampaign) {
                        return stampCampaign.id === card.stamp_campaign.id;
                      })!.multipleCards
                    : false
                }
                handleDeleteCard={() => {
                  setSelectedStampCard(card);
                  setDeleteCardOpenDialog(true);
                }}
                handleReissueCard={() => {
                  setSelectedStampCard(card);
                  handleReissueCard();
                }}
                stampCampaignList={activeStampCampaignList}
              />
            ))}
          <Typography variant="h4" className={classes.rewardHeader}>
            Rewards
          </Typography>
          {isAdminOnly && (
            <div className={classes.addRewardBox}>
              <IconButton onClick={openAddRewardDialog}>
                <AddCircleIcon className={classes.circleIcon} />
              </IconButton>
            </div>
          )}
          {collectedRewardCardList.length > 0 ? (
            <RewardCard
              rewardCardList={collectedRewardCardList}
              handleRedeemReward={setRedeemReward}
              handleDeleteReward={setDeleteReward}
              isAdminOnly={isAdminOnly}
            />
          ) : (
            <Typography variant="h6" className={classes.subText} gutterBottom>
              Earned rewards will appear here
            </Typography>
          )}
          <Typography variant="h4" className={classes.rewardHeader}>
            History
          </Typography>
          {redeemedRewardCardList.length > 3 && (
            <>
              <div className={classes.showHistoryBox}>
                <Button
                  variant="text"
                  onClick={() => setHistoryExpand(!historyExpand)}
                  className={classes.showDetailButton}
                >
                  {historyExpand ? 'Hide' : 'Show All'}
                </Button>
              </div>
              {!historyExpand && (
                <RedeemedRewardCard
                  rewardCardList={redeemedRewardCardList.slice(0, 3)}
                />
              )}
              {historyExpand && (
                <RedeemedRewardCard rewardCardList={redeemedRewardCardList} />
              )}
            </>
          )}
          {redeemedRewardCardList.length > 0 &&
            redeemedRewardCardList.length <= 3 && (
              <RedeemedRewardCard rewardCardList={redeemedRewardCardList} />
            )}

          {redeemedRewardCardList.length === 0 && (
            <Typography variant="h6" className={classes.subText} gutterBottom>
              Redeemed and expired rewards will appear here
            </Typography>
          )}
          {isAdminOnly && (
            <Button
              variant="text"
              color="primary"
              onClick={() => setDeleteMemberOpenDialog(true)}
            >
              Delete Member
            </Button>
          )}
        </div>
        {addCardDialogOpen &&
          activeStampCampaignList &&
          activeStampCampaignList.length > 0 && (
            <AddCardDialog
              open={addCardDialogOpen}
              onClose={closeAddCardDialog}
              memberId={memberId}
              onSuccess={() => {
                closeAddCardDialog();
                snackbar.showMessage('Stamp Card Added');
                setIsLoading(true);
              }}
              activeStampCampaignList={
                currentStampCampaignIdList.length > 0
                  ? reject(activeStampCampaignList, function (stampCampaign) {
                      return currentStampCampaignIdList.includes(
                        stampCampaign.id
                      );
                    })
                  : activeStampCampaignList
              }
            />
          )}
        {addStampDialogOpen && selectedStampCard && (
          <AddStampDialog
            open={addStampDialogOpen}
            onClose={closeAddStampDialog}
            stampCardId={selectedStampCard.id}
            confirmRedeem={(addStamp: number) => {
              setAddedStamp(addedStamp + addStamp);
              if (addStamp > 1) {
                setSoundFile('double-ding.mp3');
              } else {
                setSoundFile('ding.mp3');
              }
              closeAddStampDialog();
              setIsLoading(true);
              setIsPlaying(true);
              if (isVolumeMute) {
                setInterval(() => setIsPlaying(false), 5000);
              }
            }}
            stampCampaignType={
              find(activeStampCampaignList, function (stampCampaign) {
                return stampCampaign.id === selectedStampCard.stamp_campaign.id;
              })
                ? find(activeStampCampaignList, function (stampCampaign) {
                    return (
                      stampCampaign.id === selectedStampCard.stamp_campaign.id
                    );
                  })!.type
                : ''
            }
            pin={store.pin}
            maxSelectedNumber={maxSelectedNumber}
          />
        )}
        {addRewardDialogOpen && (
          <AddRewardDialog
            open={addRewardDialogOpen}
            onClose={closeAddRewardDialog}
            onSuccess={() => {
              closeAddRewardDialog();
              snackbar.showMessage('Reward Added');
              setIsLoading(true);
            }}
            memberId={memberId}
          />
        )}
        {selectedRedeemRewardCard && (
          <RedeemRewardDialog
            open={redeemRewardOpenDialog}
            onClose={() => setRedeemRewardOpenDialog(false)}
            confirmRedeem={() => {
              handleRedeemReward();
            }}
            rewardTitle={selectedRedeemRewardCard.title}
          />
        )}
        {selectedDeleteRewardCard && (
          <DeleteRewardDialog
            rewardCardId={selectedDeleteRewardCard.id}
            memberId={member.id}
            open={deleteRewardOpenDialog}
            onClose={() => setDeleteRewardOpenDialog(false)}
            confirmDelete={() => {
              handleDeleteReward();
            }}
          />
        )}
        <DeleteMemberDialog
          open={deleteMemberOpenDialog}
          onClose={() => setDeleteMemberOpenDialog(false)}
          confirmDelete={() => {
            handleDeleteMember();
          }}
        />
        <DeleteStampCardDialog
          open={deleteCardOpenDialog}
          onClose={() => setDeleteCardOpenDialog(false)}
          confirmDelete={() => {
            handleDeleteCard();
          }}
        />
        <UpdateNotesDialog
          open={editNotesOpenDialog}
          onClose={() => setEditNotesOpenDialog(false)}
          onSuccess={() => {
            setEditNotesOpenDialog(false);
            setIsLoading(true);
          }}
          prevNotes={
            member.notes && member.notes.length > 0 ? member.notes : ''
          }
          memberId={memberId}
        />
        <UpdateEmailDialog
          open={editEmailOpenDialog}
          onClose={() => setEditEmailOpenDialog(false)}
          onSuccess={() => {
            setEditEmailOpenDialog(false);
            setIsLoading(true);
          }}
          currentEmail={user.email}
          userId={user.id}
        />
        <UpdateAlternateEmailDialog
          open={editAltEmailOpenDialog}
          onClose={() => setEditAltEmailOpenDialog(false)}
          onSuccess={() => {
            setEditAltEmailOpenDialog(false);
            setIsLoading(true);
          }}
          currentAltEmail={user.altEmail}
          userId={user.id}
        />
        <UpdateMobileDialog
          open={editMobileOpenDialog}
          onClose={() => setEditMobileOpenDialog(false)}
          onSuccess={() => {
            setEditMobileOpenDialog(false);
            setIsLoading(true);
          }}
          currentMobNum={user.mobNum ? user.mobNum : ''}
          userId={user.id}
        />
        <UpdateNameDialog
          open={editNameOpenDialog}
          onClose={() => setEditNameOpenDialog(false)}
          onSuccess={() => {
            setEditNameOpenDialog(false);
            setIsLoading(true);
          }}
          fName={
            user.firstName && user.firstName.length > 0 ? user.firstName : ' '
          }
          lName={
            user.lastName && user.lastName.length > 0 ? user.lastName : ' '
          }
          userId={user.id}
        />
        <UpdateDOBDialog
          open={editDOBOpenDialog}
          onClose={() => setEditDOBOpenDialog(false)}
          onSuccess={() => {
            setEditDOBOpenDialog(false);
            setIsLoading(true);
          }}
          birthday={user.dob ? user.dob : null}
          userId={user.id}
        />
        {removeStampDialogOpen && selectedStampCard && (
          <RemoveStampDialog
            open={removeStampDialogOpen}
            onClose={() => closeRemoveStampDialog()}
            currentStamps={selectedStampCard.quantity}
            confirmRemove={(removeStamp: number) => {
              closeRemoveStampDialog();
              handleDeleteStamp(removeStamp);
            }}
          />
        )}
      </FullScreenDialog>
    );
  }
}
